import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "../i18n/config";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const BACKEND_URL = "https://api-sentinelpro.autosignsweb.com" // para subir al server
// export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;  // para uso local
export const API_URL = `${BACKEND_URL}/api`; 

export const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get locale code based on current language
export const getLocale = () => {
  const lang = i18n.language || "es";
  const localeMap = {
    es: "es-ES",
    en: "en-US",
    fr: "fr-FR",
  };
  return localeMap[lang] || "es-ES";
};

export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleString(getLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleTimeString(getLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const feetToMeters = (feet) => feet * 0.3048;
export const metersToFeet = (meters) => meters / 0.3048;

export const speakMessage = (message) => {
  if ("speechSynthesis" in window) {
    const utterance = new SpeechSynthesisUtterance(message);
    const lang = i18n.language || "es";
    const langMap = {
      es: "es-ES",
      en: "en-US",
      fr: "fr-FR",
    };
    utterance.lang = langMap[lang] || "es-ES";
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    window.speechSynthesis.speak(utterance);
  }
};

// These will be replaced by translation functions in components
// Keeping for backward compatibility, but components should use translations
export const getManualCheckReasons = (t) => [
  { value: "gps_failure", label: t("manualCheckReasons.gps_failure") },
  { value: "signal_interference", label: t("manualCheckReasons.signal_interference") },
  { value: "device_malfunction", label: t("manualCheckReasons.device_malfunction") },
  { value: "building_obstruction", label: t("manualCheckReasons.building_obstruction") },
  { value: "weather_conditions", label: t("manualCheckReasons.weather_conditions") },
  { value: "other", label: t("manualCheckReasons.other") },
];

export const getUserRoles = (t) => [
  { value: "admin", label: t("roles.admin") },
  { value: "revendedor", label: t("roles.revendedor") },
  { value: "supervisor", label: t("roles.supervisor") },
  { value: "vigilante", label: t("roles.vigilante") },
];

export const getPaymentTypes = (t) => [
  { value: "hourly", label: t("paymentTypes.hourly") },
  { value: "mixed", label: t("paymentTypes.mixed") },
];

export const getRouteOrders = (t) => [
  { value: "sequential", label: t("routeOrders.sequential") },
  { value: "random", label: t("routeOrders.random") },
];

// Legacy exports for backward compatibility (will use Spanish as fallback)
export const MANUAL_CHECK_REASONS = [
  { value: "gps_failure", label: "Falla de GPS" },
  { value: "signal_interference", label: "Interferencia de señal" },
  { value: "device_malfunction", label: "Mal funcionamiento del dispositivo" },
  { value: "building_obstruction", label: "Obstrucción por edificio" },
  { value: "weather_conditions", label: "Condiciones climáticas" },
  { value: "other", label: "Otro" },
];

export const USER_ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "revendedor", label: "Revendedor" },
  { value: "supervisor", label: "Supervisor" },
  { value: "vigilante", label: "Vigilante" },
];

export const PAYMENT_TYPES = [
  { value: "hourly", label: "Por hora" },
  { value: "mixed", label: "Mixto (Fijo + Horas)" },
];

export const ROUTE_ORDERS = [
  { value: "sequential", label: "Secuencial" },
  { value: "random", label: "Aleatorio" },
];

// Function to translate API error messages
// Maps common Spanish API error messages to translation keys
export const translateApiMessage = (message, t) => {
  if (!message || typeof message !== "string") {
    return message;
  }

  const messageLower = message.toLowerCase().trim();
  
  // Map of Spanish API messages to translation keys
  const messageMap = {
    "credenciales inválidas": t("api.errors.invalidCredentials"),
    "usuario desactivado": t("api.errors.userDeactivated"),
    "error al guardar": t("api.errors.saveError"),
    "error al eliminar": t("api.errors.deleteError"),
    "error al cargar": t("api.errors.loadError"),
    "error al cargar datos": t("api.errors.loadError"),
    "error al iniciar sesión": t("api.errors.loginError"),
    "no autorizado": t("api.errors.unauthorized"),
    "acceso denegado": t("api.errors.forbidden"),
    "recurso no encontrado": t("api.errors.notFound"),
    "error del servidor": t("api.errors.serverError"),
    "error de conexión": t("api.errors.networkError"),
    "guardado correctamente": t("api.success.saved"),
    "eliminado correctamente": t("api.success.deleted"),
    "actualizado correctamente": t("api.success.updated"),
    "creado correctamente": t("api.success.created"),
  };

  // Try exact match first
  if (messageMap[messageLower]) {
    return messageMap[messageLower];
  }

  // Try partial matches for common patterns
  if (messageLower.includes("credenciales")) {
    return t("api.errors.invalidCredentials");
  }
  if (messageLower.includes("desactivado")) {
    return t("api.errors.userDeactivated");
  }
  if (messageLower.includes("error al guardar") || messageLower.includes("error guardando")) {
    return t("api.errors.saveError");
  }
  if (messageLower.includes("error al eliminar") || messageLower.includes("error eliminando")) {
    return t("api.errors.deleteError");
  }
  if (messageLower.includes("error al cargar") || messageLower.includes("error cargando")) {
    return t("api.errors.loadError");
  }
  if (messageLower.includes("no autorizado") || messageLower.includes("unauthorized")) {
    return t("api.errors.unauthorized");
  }
  if (messageLower.includes("acceso denegado") || messageLower.includes("forbidden")) {
    return t("api.errors.forbidden");
  }
  if (messageLower.includes("no encontrado") || messageLower.includes("not found")) {
    return t("api.errors.notFound");
  }

  // If no match found, return original message
  return message;
};

// Helper function to get API error message with translation
export const getApiErrorMessage = (error, t, fallbackKey = "api.errors.unknownError") => {
  if (!error) {
    return t(fallbackKey);
  }

  // Try to get message from error response
  const apiMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
  
  if (apiMessage) {
    return translateApiMessage(apiMessage, t);
  }

  // Fallback to generic error
  return t(fallbackKey);
};
